import React, { useState, useEffect } from 'react';
import { showToast } from '../utils/toast';

/**
 * DisabledModuleDialog
 *
 * Displays helpful context when a user tries to access a disabled module.
 * Shows estimated availability, required plan, and option to request access.
 */
const DisabledModuleDialog = ({ moduleKey, onRequestAccess }) => {
  const [moduleInfo, setModuleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModuleInfo = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/v1/modules/${moduleKey}/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          // Fallback: use basic info if API call fails
          setModuleInfo(getDefaultModuleInfo(moduleKey));
          return;
        }

        const data = await response.json();
        setModuleInfo(data);
      } catch (err) {
        console.error('Failed to fetch module info:', err);
        setModuleInfo(getDefaultModuleInfo(moduleKey));
      } finally {
        setLoading(false);
      }
    };

    if (moduleKey) {
      fetchModuleInfo();
    }
  }, [moduleKey]);

  const handleRequestAccess = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/v1/modules/${moduleKey}/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: 'User requested access from disabled module dialog',
          priority: 'medium',
        }),
      });

      if (response.ok) {
        showToast('Feature request submitted. Our team will review it soon.', 'success');
        if (onRequestAccess) onRequestAccess();
      } else {
        showToast('Failed to submit request. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Failed to request module access:', err);
      showToast('Error submitting request. Please try again.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="disabled-module-dialog">
        <div className="dialog-spinner"></div>
      </div>
    );
  }

  if (!moduleInfo) {
    return (
      <div className="disabled-module-dialog">
        <div className="dialog-content">
          <span className="dialog-icon">🧩</span>
          <h2>Module Not Available</h2>
          <p>This feature is not enabled for your account.</p>
        </div>
      </div>
    );
  }

  const isAvailablePost = moduleInfo.reason === 'available_post_pilot' || moduleInfo.reason === 'post_pilot';
  const requiresPlanUpgrade = moduleInfo.required_plan && moduleInfo.required_plan !== 'free';

  return (
    <div className="disabled-module-dialog">
      <div className="dialog-content">
        <span className="dialog-icon">🧩</span>

        <h2>{moduleInfo.name || 'Feature Unavailable'}</h2>

        <p className="dialog-description">
          {moduleInfo.description || 'This module is not currently enabled for your account.'}
        </p>

        {/* Status Badge */}
        <div className="status-badge">
          <span className={`badge badge-${moduleInfo.reason}`}>
            {getStatusLabel(moduleInfo.reason)}
          </span>
        </div>

        {/* Availability Information */}
        {isAvailablePost && moduleInfo.estimated_availability && (
          <div className="info-section">
            <h3>📅 Available After Pilot</h3>
            <p>
              Expected availability: <strong>{formatDate(moduleInfo.estimated_availability)}</strong>
            </p>
          </div>
        )}

        {/* Plan Information */}
        {requiresPlanUpgrade && (
          <div className="info-section">
            <h3>📋 Plan Requirements</h3>
            <p>
              This feature is available in the <strong>{moduleInfo.required_plan}</strong> plan.
              Contact our sales team to upgrade.
            </p>
          </div>
        )}

        {/* Documentation Link */}
        {moduleInfo.documentation_url && (
          <div className="info-section">
            <h3>📚 Learn More</h3>
            <a
              href={moduleInfo.documentation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="doc-link"
            >
              Read documentation →
            </a>
          </div>
        )}

        {/* Actions */}
        <div className="dialog-actions">
          <button
            className="btn btn-secondary"
            onClick={() => window.history.back()}
          >
            ← Go Back
          </button>

          {moduleInfo.reason !== 'requires_plan' && (
            <button
              className="btn btn-primary"
              onClick={handleRequestAccess}
            >
              Request Early Access
            </button>
          )}
        </div>

        {/* Help Text */}
        <p className="dialog-help-text">
          {moduleInfo.reason === 'requires_plan'
            ? "Contact our sales team at sales@yourdomain.com to upgrade your plan."
            : "We'll notify you when this feature becomes available."}
        </p>
      </div>

      <style jsx>{`
        .disabled-module-dialog {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 20px;
        }

        .dialog-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          padding: 40px;
          max-width: 500px;
          text-align: center;
        }

        .dialog-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #e5e7eb;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 20px auto;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .dialog-icon {
          display: block;
          font-size: 48px;
          margin-bottom: 20px;
        }

        h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 12px;
        }

        .dialog-description {
          color: #6b7280;
          margin: 0 0 24px;
          line-height: 1.5;
        }

        .status-badge {
          margin: 16px 0;
        }

        .badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .badge-available_post_pilot,
        .badge-post_pilot {
          background: #dbeafe;
          color: #1e40af;
        }

        .badge-requires_plan {
          background: #fef08a;
          color: #92400e;
        }

        .badge-in_development {
          background: #e9d5ff;
          color: #6b21a8;
        }

        .info-section {
          background: #f9fafb;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          margin: 16px 0;
          text-align: left;
          border-radius: 4px;
        }

        .info-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 8px;
        }

        .info-section p {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }

        .doc-link {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
          display: inline-block;
        }

        .doc-link:hover {
          text-decoration: underline;
        }

        .dialog-actions {
          display: flex;
          gap: 12px;
          margin: 32px 0 24px;
        }

        .btn {
          flex: 1;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: #e5e7eb;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #d1d5db;
        }

        .dialog-help-text {
          font-size: 12px;
          color: #9ca3af;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

/**
 * Get default module information if API call fails
 */
function getDefaultModuleInfo(moduleKey) {
  const moduleMap = {
    analytics: {
      name: 'Analytics Dashboard',
      description: 'Advanced financial analysis and reporting.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
    ai_chat: {
      name: 'AI Chat',
      description: 'AI-powered conversational assistant.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
    ai_insights: {
      name: 'AI Insights',
      description: 'AI-driven predictive analytics.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
    ai_recommendations: {
      name: 'AI Recommendations',
      description: 'Smart recommendations engine.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
    customer_support: {
      name: 'Customer Support',
      description: 'Support automation tools.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
    tasks: {
      name: 'Tasks',
      description: 'Task management and tracking.',
      reason: 'post_pilot',
      estimated_availability: '2026-05-15',
    },
  };

  return moduleMap[moduleKey] || {
    name: 'Feature Unavailable',
    description: 'This module is not currently enabled.',
    reason: 'disabled',
  };
}

/**
 * Get human-readable status label
 */
function getStatusLabel(reason) {
  const labels = {
    'post_pilot': '📅 Post-Pilot',
    'available_post_pilot': '📅 Post-Pilot',
    'requires_plan': '📋 Plan Upgrade',
    'in_development': '🔨 In Development',
    'disabled': '❌ Disabled',
  };
  return labels[reason] || 'Unavailable';
}

/**
 * Format date string (ISO to readable)
 */
function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

export default DisabledModuleDialog;
